/**
 * Cookie + Terms consent banner for first visit
 */
import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Link, Stack, Paper } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const COOKIE_KEY = 'tripnovac_cookies';
const TERMS_KEY = 'tripnovac_terms_ack';

const CookieConsentBanner: React.FC = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const cookies = localStorage.getItem(COOKIE_KEY);
      const terms = localStorage.getItem(TERMS_KEY);
      if (!cookies || !terms) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  const acceptAll = () => {
    try {
      localStorage.setItem(COOKIE_KEY, 'accepted');
      localStorage.setItem(TERMS_KEY, 'accepted');
      localStorage.setItem('tripnovac_cookie_prefs', JSON.stringify({ essential: true, analytics: true, marketing: false }));
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  const acceptEssential = () => {
    try {
      localStorage.setItem(COOKIE_KEY, 'essential');
      localStorage.setItem(TERMS_KEY, 'accepted');
      localStorage.setItem('tripnovac_cookie_prefs', JSON.stringify({ essential: true, analytics: false, marketing: false }));
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Paper
      elevation={8}
      role="dialog"
      aria-label="Cookie and terms consent"
      sx={{
        position: 'fixed',
        left: { xs: 12, md: 24 },
        right: { xs: 12, md: 'auto' },
        bottom: { xs: 12, md: 24 },
        zIndex: 1400,
        maxWidth: 480,
        p: 2.5,
        borderRadius: 3,
        bgcolor: '#0F172A',
        color: '#F8FAFC',
        border: '1px solid rgba(255,255,255,0.12)',
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: '#F8FAFC' }}>
        Cookies & Terms
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'rgba(248,250,252,0.85)', lineHeight: 1.6 }}>
        We use essential cookies to keep you signed in and secure. By continuing, you agree to our{' '}
        <Link component={RouterLink} to="/terms" sx={{ color: '#38BDF8' }}>
          Terms
        </Link>
        ,{' '}
        <Link component={RouterLink} to="/privacy" sx={{ color: '#38BDF8' }}>
          Privacy Policy
        </Link>
        , and{' '}
        <Link component={RouterLink} to="/cookies" sx={{ color: '#38BDF8' }}>
          Cookie Policy
        </Link>
        .
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button
          variant="contained"
          onClick={acceptAll}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            bgcolor: '#0EA5E9',
            color: '#fff',
            '&:hover': { bgcolor: '#0284C7' },
          }}
        >
          Accept all
        </Button>
        <Button
          variant="outlined"
          onClick={acceptEssential}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            borderColor: 'rgba(248,250,252,0.35)',
            color: '#F8FAFC',
            '&:hover': { borderColor: '#F8FAFC', bgcolor: 'rgba(255,255,255,0.06)' },
          }}
        >
          Essential only
        </Button>
      </Stack>
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" sx={{ color: 'rgba(248,250,252,0.55)' }}>
          You can change preferences later on the Cookie Policy page.
        </Typography>
      </Box>
    </Paper>
  );
};

export default CookieConsentBanner;
